// ============================================================
// Diary helpers — pure functions + constants extracted from
// pages/Diary.js so the container and its four views can share
// the same time/format/colour logic without duplication.
// ============================================================
import {
  hoursForDate,
  applyDateException,
  computeAvailableSlots,
} from '../availability';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Day grid: dynamic start/end hours (union of working barbers' hours),
// each row = 62px = 1 hour. Falls back to 09:00 → 20:00 if nobody is
// working (e.g. all closed or empty roster).
export const DEFAULT_DAY_START_HR = 9;
export const DEFAULT_DAY_END_HR   = 20;
export const ROW_HEIGHT   = 62;
export const PX_PER_MIN   = ROW_HEIGHT / 60;
export const SLOT_GRAIN_MIN = 30;   // ghost-slot granularity

// Minimum RENDERED height (post-inset) required for every diary card
// so the three lines (time / customer / service) stay legible even
// for short bookings. When a card's natural pixel height falls below
// this the hour row it lives in expands (see `useDiaryGrid`). 58px
// matches: 5-6px top padding + ~14px (time) + ~16px (name) + ~14px
// (service) + gaps + 6-8px bottom padding.
export const MIN_CARD_H     = 58;
export const CARD_INSET_PX  = 2;   // hairline gap between back-to-back cards

// ---- Date helpers ----
export const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
export const endOfDay   = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };
export const addDays    = (d, n) => { const x = new Date(d); x.setDate(x.getDate()+n); return x; };
export const startOfWeekMon = (d) => {
  const x = startOfDay(d);
  const diff = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - diff);
  return x;
};
export const fmtTime = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};
export const fmtDay = (d) => {
  const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${day} ${d.getDate()} ${m}`;
};
export const fmtDayShort = (d) => {
  const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  return `${day} ${d.getDate()}`;
};
export const fmtMoney = (n) => `£${Number(n || 0).toFixed(0)}`;

// Local YYYY-MM-DD key that matches Postgres `date` columns without
// getting bitten by UTC drift (barber_date_exceptions.date is a wall date).
export const dateKeyOf = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// "HH:MM(:SS)" wall time → minutes past midnight. Matches the parser
// used in availability.js so break overlays line up with ghost slots.
export const parseWallToMin = (t) => {
  if (t == null) return null;
  const [hh, mm] = String(t).split(':').map(Number);
  return hh * 60 + (mm || 0);
};

// Convert an ISO booking timestamp to a minute-of-day using the
// viewer's LOCAL timezone (same source that BookingDetailPanel /
// availability.js / the New Booking modal all use). Reading the raw
// UTC HH:MM out of the string diverged from the detail panel: a
// London-BST booking stored as `T19:20:00Z` renders locally as
// 20:20 in the panel, so the card must too.
export const parseIsoMin = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return d.getHours() * 60 + d.getMinutes();
};

// Formats a booking's wall-clock range straight from the ISO string
// (timezone-independent, same source as `parseIsoMin`).
export const fmtRange = (b, formatWallMin) =>
  `${formatWallMin(parseIsoMin(b.starts_at))}–${formatWallMin(parseIsoMin(b.ends_at))}`;

// Colour class for the event card. Ordering matters:
//   no_show           → amber   (reserved strictly for no-shows)
//   cancelled         → red     (matches the ev-status-cancelled overlay)
//   first-time client → new     (white fill, fixed indigo outline —
//                                 loses to no-show / cancelled above)
//   manual / walk-in  → teal    (distinct from Completed green so a
//                                 completed walk-in still reads as
//                                 "done" via the ev-status-completed
//                                 !important overlay, not as walk-in)
//   else (online)     → blue
// Note: `ev-status-completed` in CSS uses `!important` and therefore
// trumps every base colour here — a completed booking is always green.
export const colourFor = (b, isFirstForCustomer) => {
  if (b.status === 'no_show')    return 'amber';
  if (b.status === 'cancelled')  return 'red';
  if (isFirstForCustomer)        return 'new';
  if (b.channel === 'manual')    return 'teal';
  return 'blue';
};

/** Outstanding £ still owing on a single booking. Zero for prepaid /
    paid-in-full bookings, price − deposit for deposit-only, and full
    price for unpaid bookings that require a deposit. */
export const owingFor = (b) => {
  if (b.paid_in_full) return 0;
  const price = Number(b.service?.price ?? 0);
  const deposit = Number(b.deposit_amount ?? 0);
  return Math.max(0, price - (b.deposit_paid ? deposit : 0));
};

// Whole-card recolour based on lifecycle status. Confirmed/pending
// bookings keep the channel-driven colour (colourFor) so barbers can
// see at-a-glance where a booking came from. Anything that has
// resolved (completed / no-show / cancelled) takes on that status's
// tint everywhere — background, border, opacity — so the diary reads
// as a story rather than a wall of blue cards.
export const statusClassFor = (b) => {
  if (b.status === 'completed') return 'ev-status-completed';
  if (b.status === 'no_show')   return 'ev-status-noshow';
  if (b.status === 'cancelled') return 'ev-status-cancelled';
  return '';
};

// Fires `PATCH /api/bookings/{id}/status`. Reloads on success so both
// the event card and the totals update. Called from the kebab menu.
export const patchBookingStatus = async (bookingId, status, authToken) => {
  const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Status update failed (${res.status}): ${t}`);
  }
  return res.json();
};

// Fires POST /api/bookings/{id}/payment. Similar shape to
// patchBookingStatus so the kebab menu has one uniform call path.
export const patchBookingPayment = async (bookingId, patch, authToken) => {
  const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Payment update failed (${res.status}): ${t}`);
  }
  return res.json();
};

// Compute the free ghost slots for one barber on one date
export const ghostSlotsFor = (
  barberId, date, hoursByBarber, bookingsForBarber,
  breaksForBarberDate, exceptionsForBarber, bufferMin = 0
) => {
  const weekly = hoursForDate(hoursByBarber, barberId, date);
  const exc = exceptionsForBarber?.[dateKeyOf(date)];
  const h = applyDateException(weekly, exc);
  const starts = computeAvailableSlots({
    hours: h,
    durationMin: SLOT_GRAIN_MIN,
    bookings: bookingsForBarber,
    breaks: breaksForBarberDate || [],
    stepMin: SLOT_GRAIN_MIN,
    bufferMin,
  });
  return starts.map(startMin => ({ startMin, endMin: startMin + SLOT_GRAIN_MIN }));
};
